"use client";

import React from "react";
import { Box } from "@mui/material";
import { PrivateTouringPage } from "@/types/dto/travel-styles/private-touring";
import BasePageHero from "../common/page-hero/base-page-hero/base-page-hero";
import PageHero from "../common/page-hero/page-hero";
import ContactFormSectionComponent from "../forms/contact-form-section/contact-form-section";
import PrivateTourStepper from "./private-tours/private-tour-stepper";
import TermsAndConditions from "../common/terms-and-conditions";
import PlacesToDiscover from "../homepage/places-to-discover/places-discover-container";
import PrivateTourPricing from "./private-tour-pricing-section";
import createVideoSectionSlotProps from "./create-video-section-slot-props";

const PrivateTouringLayout = ({
  heroSection,
  privateTouringWork,
  disclaimerText,
  destinationSection,
  formSection,
  guestListVideo,
  videoSection,
  privateTourPricing
}: PrivateTouringPage) => {
  let rank = 0;
  const nextRank = () => {
    rank += 1;
    return rank;
  };

  const videoSectionSlotProps = createVideoSectionSlotProps({
    heroHeader: {
      sx: {
        fontSize: "2.8rem !important"
      }
    },
    innerTextContainer: {
      sx: {
        alignItems: { xs: "center", lg: "flex-start" }
      }
    },
    mediaContainer: {
      sx: {
        maxWidth: {
          lg: "514px"
        }
      }
    }
  });

  const guestListVideoSlotProps = createVideoSectionSlotProps({
    rootContainer: {
      removePatternBg: true
    },
    heroHeader: {
      sx: {
        fontSize: "2.8rem !important"
      }
    },
    textContent: {
      sx: {
        alignSelf: "center"
      }
    },
    mediaContainer: {
      sx: {
        maxWidth: {
          lg: "514px"
        }
      }
    }
  });

  return (
    <Box>
      {heroSection && heroSection?.backgroundImage?.imageUrl && heroSection.basicInfo && (
        <BasePageHero
          dataModuleName="Private Touring Header"
          moduleRankingPosition={String(nextRank())}
          dataParentContainerType="page hero"
          imageSrc={heroSection.backgroundImage.imageUrl}
          imageAlt={
            heroSection.backgroundImage.altText || "Private Touring header background image"
          }
          pageTitle={heroSection.basicInfo.headerText || "Private Touring Default Heading"}
          basicInfoMultipleCtas={heroSection.basicInfo}
          variant="text"
          testId="travel-styles"
          slotProps={{
            container: {
              sx: { minHeight: { xs: "45rem", md: "45rem", lg: "60rem" } }
            },
            backgroundImage: {
              sx: {
                background:
                  "linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 100%)"
              }
            },
            header: {
              sx: { paddingX: { xs: "2rem" } }
            },
            sectionHeader: {
              copySx: {
                paddingX: { xs: "2rem" }
              }
            }
          }}
        />
      )}
      {privateTouringWork && (
        <PrivateTourStepper privateTouringWork={privateTouringWork} moduleRankingPosition={nextRank()} />
      )}

      {videoSection && videoSection.basicInfo && (
        <PageHero
          testId="video-section"
          heroBasicInfo={videoSection.basicInfo}
          headerImage={videoSection.backgroundImage}
          video={
            videoSection.video
              ? {
                  videoId: videoSection.video.videoId,
                  videoTitle: videoSection.video.title
                }
              : undefined
          }
          padding={{
            xs: "2rem 2.4rem 4rem 2.4rem",
            mobile: "4rem 3.2rem 2rem 3.2rem",
            tablet: "4rem 3.2rem 2rem 3.2rem",
            desktop: "6rem 10rem 3rem 10rem"
          }}
          moduleRankingPosition={String(nextRank())}
          mediaLeft={videoSection.design === "Media Left"}
          slotProps={videoSectionSlotProps}
          headerLevel="h2"
          dataModuleName="Media Text and CTA"
        />
      )}

      {guestListVideo && guestListVideo.basicInfo && (
        <PageHero
          testId="guest-list-video"
          heroBasicInfo={guestListVideo.basicInfo}
          headerImage={guestListVideo.backgroundImage}
          video={
            guestListVideo.video
              ? {
                  videoId: guestListVideo.video.videoId,
                  videoTitle: guestListVideo.video.title
                }
              : undefined
          }
          moduleRankingPosition={String(nextRank())}
          mediaLeft={guestListVideo.design === "Media Left"}
          padding={{
            xs: "2rem 2.4rem 4rem 2.4rem",
            mobile: "2rem 3.2rem 4rem 3.2rem",
            tablet: "2rem 3.2rem 4rem 3.2rem",
            desktop: "3rem 10rem 6rem 10rem"
          }}
          slotProps={guestListVideoSlotProps}
          headerLevel="h2"
          dataModuleName="Media Text with CTA"
        />
      )}

      {privateTourPricing && (
        <PrivateTourPricing tourData={privateTourPricing} moduleRankingPosition={nextRank()} />
      )}
      {formSection && (
        <ContactFormSectionComponent contactFormSection={formSection} moduleRankingPosition={String(nextRank())} />
      )}

      {destinationSection && (
        <PlacesToDiscover
          placesData={destinationSection}
          moduleRankingPosition={nextRank()}
          bgColor="#fff"
        />
      )}
      {disclaimerText && (
        <TermsAndConditions
          copyText={disclaimerText}
          moduleRankingPosition={String(nextRank())}
          testId="private-tour-text-block-container"
          copyTestId="private-tour-text-block-copy"
          dataParentContainer="private-tours-layout"
          dataModuleName="private tour text block"
        />
      )}
    </Box>
  );
};

export default PrivateTouringLayout;
